import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { ThemeAttributes } from 'dashboard/models/theme';
import { ThemeSet, ThemeSetAttributes } from 'dashboard/models/theme-set';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  mergeStepsPreview,
  setInquiryTemplateVersion,
  setLocale,
  setStepsPreview,
  setStylesMap,
  setThemeSet,
} from 'dashboard/redux/slices/theme-set';
import {
  fetchPartialStepsPreview,
  fetchStepsPreview,
  snakeCaseFromKebabCaseKeys,
} from 'dashboard/redux/thunk-actions/inquiry-template';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';

export const loadThemeSet =
  (themeSetId: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { data: themeSet } = await SessionClientDashboard.getResource<ThemeSet>(
      ApiEndpoint.ThemeSet,
      themeSetId,
    );
    dispatch(setThemeSet(themeSet));
  };

export const updateThemeSet =
  (
    themeSet: ThemeSet,
    themeSetAttributes: Partial<ThemeSetAttributes>,
    deleteDarkTheme?: boolean,
  ): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { data: updatedThemeSet } = await SessionClientDashboard.updateResource<ThemeSet>(
      {
        type: themeSet.type,
        id: themeSet.id,
        attributes: {
          ...themeSetAttributes,
        },
      },
      { meta: { ['delete-dark-theme']: deleteDarkTheme ?? false } },
    );
    dispatch(showMessage('Successfully updated Theme Set', MessageType.Success));
    dispatch(setThemeSet(updatedThemeSet));
  };

export const loadThemeSetInquiryTemplateVersion =
  (inquiryTemplateVersionId: string): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { data: inquiryTemplateVersion } =
      await SessionClientDashboard.getResource<InquiryTemplateVersion>(
        ApiEndpoint.InquiryTemplateVersion,
        inquiryTemplateVersionId,
      );

    dispatch(setInquiryTemplateVersion(inquiryTemplateVersion));
  };

export const loadThemeSetStepsPreview =
  (
    inquiryTemplateVersionId: string,
    locale: InquiryIso6391LanguageCode,
  ): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const stepsPreview = await fetchStepsPreview(inquiryTemplateVersionId, locale, {
      noITVThemeSet: true,
    });
    dispatch(setLocale(locale));
    dispatch(setStepsPreview(stepsPreview));
  };

export const loadPartialStepsPreview =
  (
    inquiryTemplateVersionId: string,
    locale: InquiryIso6391LanguageCode,
    themeSet: ThemeSet,
    startPaginationIndex: number,
    endPaginationIndex: number,
  ): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const stepsPreview = await fetchPartialStepsPreview(
      inquiryTemplateVersionId,
      locale,
      themeSet,
      startPaginationIndex,
      endPaginationIndex,
    );

    dispatch(setLocale(locale));
    dispatch(mergeStepsPreview({ startPaginationIndex, partialStepsPreview: stepsPreview }));
  };

export interface LoadStylesMapParams {
  themeAttributes?: ThemeAttributes;
  themeSet?: ThemeSet;
}

export const loadStylesMap =
  (loadStyleMapParam: LoadStylesMapParams): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { meta: preview } = await SessionClientDashboard.postResourceSingletonActionNew<
      ThemeSet,
      {
        type: ApiModel.ThemeSet;
        attributes: {
          'theme-attributes': ThemeAttributes | undefined;
          'theme-set': ThemeSet | undefined;
        };
      }
    >(ApiEndpoint.ThemeSet, 'inquiry-template-styles-schema', {
      type: ApiModel.ThemeSet,
      attributes: {
        'theme-attributes': loadStyleMapParam.themeAttributes,
        'theme-set': loadStyleMapParam.themeSet,
      },
    });
    if (preview) {
      const snakifiedPreview = {
        'components-styles': snakeCaseFromKebabCaseKeys(
          preview['components-styles'] as unknown as Record<string, unknown>,
        ),
        'steps-styles': snakeCaseFromKebabCaseKeys(
          preview['steps-styles'] as unknown as Record<string, unknown>,
        ),
      };
      dispatch(setStylesMap(snakifiedPreview));
    }
  };
